/**
 * How far opening a baseline has got, drawn where it was asked for (issue #97,
 * phase D).
 *
 * Opening is slow, so the person sees WHAT it is doing - the server's phase - and,
 * when the phase has a count, a bar. A failure is not an error wall: it says why in
 * the server's words and says the thing that still works, the baseline's pipeline
 * artifacts, which are read from GitLab and need none of this. The phase and the
 * message are plain text.
 */

import Alert from '@mui/material/Alert'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { BaselineStatus } from '../../schema'

export default function BaselineOpening({ status }: { status: BaselineStatus }) {
  if (status.state === 'failed') {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        {`Could not open ${status.tag}: ${status.message || 'no reason was given'}. `}
        Its pipeline artifacts are still available - use Artifacts on its row.
      </Alert>
    )
  }
  if (status.state === 'ready') return null

  const progress = status.progress
  const done = typeof progress?.done === 'number' ? progress.done : null
  const total = typeof progress?.total === 'number' && progress.total > 0 ? progress.total : null
  const counted = done !== null && total !== null
  const phase = progress?.phase || (status.state === 'opening' ? 'Opening' : 'Starting')
  return (
    <Stack spacing={0.5} sx={{ mt: 1, maxWidth: 420 }} data-testid="baseline-opening">
      <Typography variant="caption" color="text.secondary">
        {counted ? `${phase}: ${done} of ${total}` : `${phase}…`}
      </Typography>
      <LinearProgress
        aria-label={`Opening ${status.tag}`}
        variant={counted ? 'determinate' : 'indeterminate'}
        value={counted ? Math.min(100, (done / total) * 100) : undefined}
      />
    </Stack>
  )
}
